import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid, Legend } from "recharts";
import { REFUSAL_OPTIONS } from "./QualifyStatusModal";

// VAGUE 43 — Tableau de performance partagé (portail télépro + admin).
const KPIS = [
  ["calls", "Appels effectués"], ["reached", "Prospects joints"], ["not_reached", "Non joints"],
  ["callbacks", "Rappels programmés"], ["interested", "Intéressés"], ["sales", "Abonnements conclus"],
  ["website_leads", "Leads sites internet"], ["refusals", "Refus"],
];
const RATES = [["contact_rate", "Taux de contact"], ["conversion_rate", "Conversion sur joints"]];
const REASON_LABELS = { ...Object.fromEntries(REFUSAL_OPTIONS.map((o) => [o.id, o.label])), no_more_contact: "Ne souhaite pas être rappelé", unspecified: "Sans motif (ancien)" };
const fmtRate = (v) => (v === null || v === undefined ? "—" : `${String(v).replace(".", ",")} %`);
const fr = (iso) => iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit" }) : "";

function Delta({ cur, prev, rate }) {
  if (cur === null || prev === null || cur === undefined || prev === undefined) return null;
  const d = Math.round((cur - prev) * 10) / 10;
  if (!d) return <span className="text-[10px] text-muted-foreground">= stable</span>;
  return <span className={`text-[10px] font-bold ${d > 0 ? "text-primary" : "text-secondary"}`}>{d > 0 ? "+" : ""}{String(d).replace(".", ",")}{rate ? " pt" : ""}</span>;
}

function PeriodBlock({ label, period, testid }) {
  const { current: c, previous: p } = period;
  return (
    <div data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">{label} <span className="normal-case font-normal">(vs période précédente)</span></div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {RATES.map(([k, l]) => (
          <div key={k} className="bg-primary/5 border border-primary/20 rounded-sm p-2.5" data-testid={`${testid}-${k}`}>
            <div className="text-[10px] text-muted-foreground">{l}</div>
            <div className="font-display text-xl">{fmtRate(c[k])}</div>
            <Delta cur={c[k]} prev={p[k]} rate/>
          </div>
        ))}
        {KPIS.map(([k, l]) => (
          <div key={k} className="bg-card border border-border rounded-sm p-2.5" data-testid={`${testid}-${k}`}>
            <div className="text-[10px] text-muted-foreground">{l}</div>
            <div className="font-display text-xl">{c[k]}</div>
            <Delta cur={c[k]} prev={p[k]}/>
          </div>
        ))}
      </div>
    </div>
  );
}

function Comparison({ comp, start }) {
  const rows = [...RATES, ...KPIS];
  return (
    <div data-testid="perf-comparison">
      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">
        Avant / après la nouvelle stratégie ({fr(start)}) — {comp.days} j de chaque côté
      </div>
      <table className="w-full text-xs">
        <thead><tr className="text-muted-foreground"><th className="text-left font-medium py-1">Indicateur</th><th className="text-right font-medium">Avant</th><th className="text-right font-medium">Après</th></tr></thead>
        <tbody>
          {rows.map(([k, l]) => {
            const isRate = k.endsWith("_rate");
            return (
              <tr key={k} className="border-t border-border/60" data-testid={`perf-comparison-${k}`}>
                <td className="py-1.5">{l}</td>
                <td className="text-right font-mono">{isRate ? fmtRate(comp.before[k]) : comp.before[k]}</td>
                <td className="text-right font-mono font-bold">{isRate ? fmtRate(comp.after[k]) : comp.after[k]}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function PerformanceDashboard({ data }) {
  const reasons = data.periods.d30.current.refusals_by_reason;
  const startInRange = data.strategy_start && data.series.some((s) => s.date === data.strategy_start.slice(0, 10));
  return (
    <div className="space-y-5" data-testid="performance-dashboard">
      <PeriodBlock label="7 derniers jours" period={data.periods.d7} testid="perf-d7"/>
      <PeriodBlock label="30 derniers jours" period={data.periods.d30} testid="perf-d30"/>
      <div data-testid="perf-chart">
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">Évolution sur 30 jours</div>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))"/>
              <XAxis dataKey="date" tickFormatter={fr} tick={{ fontSize: 10 }} interval="preserveStartEnd"/>
              <YAxis allowDecimals={false} tick={{ fontSize: 10 }}/>
              <Tooltip labelFormatter={fr}/>
              <Legend wrapperStyle={{ fontSize: 11 }}/>
              <Line type="monotone" dataKey="calls" name="Appels" stroke="#134E34" strokeWidth={2} dot={false}/>
              <Line type="monotone" dataKey="reached" name="Joints" stroke="#1a5f8a" strokeWidth={2} dot={false}/>
              <Line type="monotone" dataKey="sales" name="Ventes" stroke="#C8795A" strokeWidth={2} dot={false}/>
              {startInRange && <ReferenceLine x={data.strategy_start.slice(0, 10)} stroke="#E0B043" strokeDasharray="4 4" label={{ value: "Nouvelle stratégie", fontSize: 10, position: "insideTopRight" }}/>}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div data-testid="perf-refusals">
        <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2">Refus par catégorie (30 j)</div>
        <div className="flex flex-wrap gap-1.5">
          {Object.entries(reasons).map(([k, v]) => (
            <span key={k} className="text-[11px] border border-border rounded-sm px-2 py-1 bg-card">{REASON_LABELS[k] || k} : <b>{v}</b></span>
          ))}
        </div>
      </div>
      {data.comparison ? <Comparison comp={data.comparison} start={data.strategy_start}/> : (
        <p className="text-xs text-muted-foreground" data-testid="perf-no-comparison">
          La comparaison avant / après apparaîtra dès les premiers appels qualifiés avec la nouvelle stratégie.
        </p>
      )}
    </div>
  );
}
